import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/store/store'
import type { ContentAudio } from '../../common/types'
import type { LexicalDraft } from '../workflow/sentenceFlowModel'

interface SentenceFlowState {
  open: boolean
  readingId: string | null
  sentenceIds: string[]
  index: number
  steps: Record<string, number>
  furthestSteps: Record<string, number>
  drafts: Record<string, LexicalDraft[]>
  completed: Record<string, boolean>
  pronunciationChecked: Record<string, boolean>
  lexicalVoices: Record<string, string>
  lexicalVoiceOverrides: Record<string, string>
  lexicalAudio: Record<string, ContentAudio[]>
}

const initialState: SentenceFlowState = {
  open: false,
  readingId: null,
  sentenceIds: [],
  index: 0,
  steps: {},
  furthestSteps: {},
  drafts: {},
  completed: {},
  pronunciationChecked: {},
  lexicalVoices: {},
  lexicalVoiceOverrides: {},
  lexicalAudio: {},
}

const sentenceFlowSlice = createSlice({
  name: 'readingSentenceFlow',
  initialState,
  reducers: {
    openSentenceFlow(state, action: PayloadAction<{ readingId: string; sentenceIds: string[]; sentenceId?: string }>) {
      const { readingId, sentenceIds, sentenceId } = action.payload
      const sameReading = state.readingId === readingId
      const currentSentenceId = sameReading ? state.sentenceIds[state.index] : undefined
      if (!sameReading) {
        state.steps = {}
        state.furthestSteps = {}
        state.drafts = {}
        state.completed = {}
        state.pronunciationChecked = {}
        state.lexicalVoices = {}
        state.lexicalVoiceOverrides = {}
        state.lexicalAudio = {}
      }
      state.open = true
      state.readingId = readingId
      state.sentenceIds = sentenceIds
      for (const id of sentenceIds) {
        state.steps[id] = Math.min(state.steps[id] ?? 0, 4)
        state.furthestSteps[id] = Math.min(state.furthestSteps[id] ?? 0, 4)
      }
      const targetSentenceId = sentenceId || currentSentenceId
      state.index = Math.max(0, targetSentenceId ? sentenceIds.indexOf(targetSentenceId) : 0)
    },
    closeSentenceFlow(state) { state.open = false },
    moveSentenceFlow(state, action: PayloadAction<number>) {
      state.index = Math.max(0, Math.min(state.sentenceIds.length - 1, state.index + action.payload))
    },
    skipSentenceFlow(state) {
      if (state.index < state.sentenceIds.length - 1) state.index += 1
      else state.open = false
    },
    setSentenceFlowStep(state, action: PayloadAction<number>) {
      const id = state.sentenceIds[state.index]
      if (id) {
        state.steps[id] = Math.max(0, Math.min(4, action.payload))
        state.furthestSteps[id] = Math.max(state.furthestSteps[id] ?? 0, state.steps[id])
        if (state.steps[id] === 4) state.completed[id] = true
      }
    },
    setSentenceFlowDrafts(state, action: PayloadAction<{ sentenceId: string; drafts: LexicalDraft[] }>) {
      const { sentenceId, drafts } = action.payload
      state.drafts[sentenceId] = drafts
      if (drafts.length) {
        state.furthestSteps[sentenceId] = Math.min(state.furthestSteps[sentenceId] ?? 1, 1)
        delete state.completed[sentenceId]
      }
    },
    setSentenceFlowVoice(state, action: PayloadAction<{ sentenceId: string; voice: string }>) {
      state.lexicalVoices[action.payload.sentenceId] = action.payload.voice
    },
    setLexicalFlowVoice(state, action: PayloadAction<{ lexicalId: string; voice: string }>) {
      state.lexicalVoiceOverrides[action.payload.lexicalId] = action.payload.voice
    },
    setLexicalFlowAudio(state, action: PayloadAction<{ lexicalId: string; audio: ContentAudio[] }>) {
      state.lexicalAudio[action.payload.lexicalId] = action.payload.audio
    },
    setLexicalFlowAudioBatch(state, action: PayloadAction<Record<string, ContentAudio[]>>) {
      Object.assign(state.lexicalAudio, action.payload)
    },
    markSentencePronunciationChecked(state, action: PayloadAction<{ sentenceId: string; checked: boolean }>) {
      state.pronunciationChecked[action.payload.sentenceId] = action.payload.checked
      if (!action.payload.checked) {
        delete state.completed[action.payload.sentenceId]
        state.furthestSteps[action.payload.sentenceId] = Math.min(state.furthestSteps[action.payload.sentenceId] ?? 2, 2)
        if ((state.steps[action.payload.sentenceId] ?? 0) > 2) state.steps[action.payload.sentenceId] = 2
      }
    },
    finishSentenceFlow(state) {
      const id = state.sentenceIds[state.index]
      if (id) state.completed[id] = true
      if (state.index < state.sentenceIds.length - 1) state.index += 1
    },
  },
})

export const {
  openSentenceFlow, closeSentenceFlow, moveSentenceFlow, skipSentenceFlow, setSentenceFlowStep,
  setSentenceFlowDrafts, setSentenceFlowVoice, finishSentenceFlow,
  setLexicalFlowVoice,
  setLexicalFlowAudio,
  setLexicalFlowAudioBatch,
  markSentencePronunciationChecked,
} = sentenceFlowSlice.actions

export const selectSentenceFlow = (state: RootState) => state.readingSentenceFlow
export default sentenceFlowSlice.reducer
